import type {
  AuthenticationResponseJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import type { Framework } from '@toeverything/infra';

import {
  AuthProvider,
  type WebAuthnAuthenticationOptionsResult,
  type WebAuthnCredentialSummary,
  type WebAuthnLoginVerifiedResult,
  type WebAuthnRegistrationOptionsResult,
  type WebAuthnRegistrationVerifiedResult,
} from '../provider/auth';
import { ServerScope } from '../scopes/server';
import { FetchService } from '../services/fetch';

export function configureDefaultAuthProvider(framework: Framework) {
  framework.scope(ServerScope).override(AuthProvider, resolver => {
    const fetchService = resolver.get(FetchService);
    return {
      async signInMagicLink(
        email: string,
        token: string,
        clientNonce?: string
      ) {
        const res = await fetchService.fetch('/api/auth/magic-link', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, token, client_nonce: clientNonce }),
        });
        const data = (await res.json()) as {
          localDataLoginProof?: string;
          localDataAccountId?: string;
          requiresTwoFactor?: boolean;
          preferredMethod?: 'totp' | 'email';
        };
        return {
          localDataLoginProof: data.localDataLoginProof,
          localDataAccountId: data.localDataAccountId,
          requiresTwoFactor: data.requiresTwoFactor,
          preferredMethod: data.preferredMethod,
        };
      },

      async signInOauth(
        code: string,
        state: string,
        _provider: string,
        clientNonce?: string
      ) {
        const res = await fetchService.fetch('/api/oauth/callback', {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          body: JSON.stringify({ code, state, client_nonce: clientNonce }),
          headers: {
            'content-type': 'application/json',
          },
        });
        const data = (await res.json()) as {
          redirectUri?: string;
          isNewUser?: boolean;
          localDataLoginProof?: string;
          localDataAccountId?: string;
          requiresTwoFactor?: boolean;
          preferredMethod?: 'totp' | 'email';
          user?: { email?: string };
        };
        return {
          redirectUri: data.redirectUri,
          isNewUser: data.isNewUser,
          localDataLoginProof: data.localDataLoginProof,
          localDataAccountId: data.localDataAccountId,
          requiresTwoFactor: data.requiresTwoFactor,
          preferredMethod: data.preferredMethod,
          email: data.user?.email,
        };
      },
      async signInPassword(credential: {
        email: string;
        password: string;
        verifyToken?: string;
        challenge?: string;
      }) {
        const headers: Record<string, string> = {};

        // sl-wwta.3.2: Send ALTCHA PoW solution payload
        if (credential.verifyToken) {
          headers['x-altcha-payload'] = credential.verifyToken;
        }

        const res = await fetchService.fetch('/api/auth/sign-in', {
          method: 'POST',
          body: JSON.stringify(credential),
          headers: {
            'content-type': 'application/json',
            ...headers,
          },
        });

        const data = (await res.json()) as {
          localDataLoginProof?: string;
          localDataAccountId?: string;
          requiresTwoFactor?: boolean;
          preferredMethod?: 'totp' | 'email';
          workspacesRequiring2FA?: Array<{
            workspaceId: string;
            workspaceName: string;
            enforcedAt: string | null;
            gracePeriodActive: boolean;
          }>;
        };

        return {
          localDataLoginProof: data.localDataLoginProof,
          localDataAccountId: data.localDataAccountId,
          requiresTwoFactor: data.requiresTwoFactor,
          preferredMethod: data.preferredMethod,
          workspacesRequiring2FA: data.workspacesRequiring2FA,
        };
      },
      async signOut() {
        await fetchService.fetch('/api/auth/sign-out');
      },

      // sl-wq59.6.4: WebAuthn passkey flows.
      // Every `*Options` call returns a `challengeRef` that MUST be echoed
      // on the corresponding `*Verify` call — the server consumes the
      // challenge atomically via Redis GETDEL (sl-wq59.6.2).
      async webauthnRegisterOptions(): Promise<WebAuthnRegistrationOptionsResult> {
        const res = await fetchService.fetch(
          '/api/auth/webauthn/register/options',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
          }
        );
        return (await res.json()) as WebAuthnRegistrationOptionsResult;
      },

      async webauthnRegisterVerify(
        challengeRef: string,
        response: RegistrationResponseJSON,
        deviceName?: string
      ): Promise<WebAuthnRegistrationVerifiedResult> {
        const res = await fetchService.fetch(
          '/api/auth/webauthn/register/verify',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ challengeRef, response, deviceName }),
          }
        );
        return (await res.json()) as WebAuthnRegistrationVerifiedResult;
      },

      async webauthnLoginOptions(): Promise<WebAuthnAuthenticationOptionsResult> {
        const res = await fetchService.fetch(
          '/api/auth/webauthn/login/options',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
          }
        );
        return (await res.json()) as WebAuthnAuthenticationOptionsResult;
      },

      async webauthnLoginVerify(
        challengeRef: string,
        response: AuthenticationResponseJSON
      ): Promise<WebAuthnLoginVerifiedResult> {
        const res = await fetchService.fetch(
          '/api/auth/webauthn/login/verify',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ challengeRef, response }),
          }
        );
        return (await res.json()) as WebAuthnLoginVerifiedResult;
      },

      async webauthnListCredentials(): Promise<WebAuthnCredentialSummary[]> {
        const res = await fetchService.fetch('/api/auth/webauthn/credentials', {
          method: 'GET',
          headers: { 'content-type': 'application/json' },
        });
        const data = (await res.json()) as {
          credentials: WebAuthnCredentialSummary[];
        };
        return data.credentials;
      },

      async webauthnRemoveCredential(credentialId: string): Promise<void> {
        await fetchService.fetch(
          `/api/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`,
          { method: 'DELETE' }
        );
      },
    };
  });
}
